import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

// css and images
import Add from "../../assets/add.svg";
// gobal
import { accountDataStore, userStore } from "../../store/Store";
// components
import Loading from "../../components/Loading";
import ErrorDisplay from "../../components/ErrorDisplay";
import AccountCustomerInvoiceList from "./customer/AccountCustomerInvoiceList";
import AddAccountCustomerInvoice from "./customer/AddAccountCustomerInvoice";
import AccountVendorInvoiceList from "./vendor/AccountVendorInvoiceList";
import { a11yProps, CustomTabPanel } from "../../components/TabPanels";
import AddAccountCustomerTieredInvoice from "./customer/AddAccountCustomerTieredInvoice";

export default function AccountInvoiceTabs({ type }) {
  // gobal
  const setAccountCustomerInvoices = accountDataStore(
    (data) => data.setAccountCustomerInvoices
  );
  const setAccountCustomerInvoiceTotals = accountDataStore(
    (data) => data.setAccountCustomerInvoiceTotals
  );
  const setAccountVendorInvoices = accountDataStore(
    (data) => data.setAccountVendorInvoices
  );
  const setAccountVendorInvoiceTotals = accountDataStore(
    (data) => data.setAccountVendorInvoiceTotals
  );
  const currentCompany = userStore((data) => data.currentCompany);
  const JwtToken = userStore((data) => data.JwtToken);
  const setAuthStatus = userStore((data) => data.setAuthStatus);

  // local
  const [value, setValue] = useState(0);
  const [trigger, setTrigger] = useState(false);
  const [tierTrigger, setTierTrigger] = useState(false);
  const [isAsset, setIsAsset] = useState(false);
  const [isTier, setIsTier] = useState(false);
  const [custPending, setCustPending] = useState(true);
  const [vendPending, setVendPending] = useState(true);
  const [error, setError] = useState(null);
  const [code, setCode] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentCompany.full_access) {
      setCustPending(true);
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);
      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };

      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/account-customer-invoices`,
        requestOptions
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              navigate("/login");
              setCustPending(false);
              return Promise.resolve();
            } else {
              setError("Something went wrong");
              setCode(res.status);
              setCustPending(false);
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            setCustPending(false);
            return;
          } else {
            setCustPending(false);
            setAccountCustomerInvoices(data.customer_invoices);
            setAccountCustomerInvoiceTotals(data.customer_invoice_totals)
            return;
          }
        })
        .catch((err) => {
          setCustPending(false);
          setError("Something went wrong");
          setCode(500);
          console.log(err);
          return;
        })
        .finally(() => setCustPending(false));
      return () => controller.abort();
    } else {
      setCustPending(false);
    }
  }, [
    currentCompany,
    navigate,
    JwtToken,
    setAccountCustomerInvoices,
    setAuthStatus,
    setAccountCustomerInvoiceTotals
  ]);

  useEffect(() => {
    if (currentCompany.full_access) {
      setVendPending(true);
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);
      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };

      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/account-vendor-invoices`,
        requestOptions
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              navigate("/login");
              setVendPending(false);
              return Promise.resolve();
            } else {
              setError("Something went wrong");
              setCode(res.status);
              setVendPending(false);
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            setVendPending(false);
            return;
          } else {
            setVendPending(false);
            setAccountVendorInvoices(data.vendor_invoices);
            setAccountVendorInvoiceTotals(data.vendor_invoice_totals)
            return;
          }
        })
        .catch((err) => {
          setVendPending(false);
          setError("Something went wrong");
          setCode(500);
          console.log(err);
          return;
        })
        .finally(() => setVendPending(false));
      return () => controller.abort();
    } else {
      setVendPending(false);
    }
  }, [
    currentCompany,
    navigate,
    JwtToken,
    setAccountVendorInvoices,
    setAuthStatus,
    setAccountVendorInvoiceTotals
  ]);

  const handleChange = (_, newValue) => {
    setValue(newValue);
    switch (newValue) {
      case 1:
        setIsAsset(true);
        setIsTier(false);
        break;
      case 2:
        setIsAsset(false);
        setIsTier(true);
        break;
      default:
        setIsAsset(false);
        setIsTier(false);
    }
  };

  if (custPending || vendPending) {
    return <Loading />;
  } else if (error) {
    return <ErrorDisplay code={code} message={"Could not retrieve invoices"} />;
  } else {
    return (
      <>
        <Box
          sx={{
            width: "100%",
            margin: "20px 0 0 0",
            padding: "0",
          }}
        >
          <div className="d-flex justify-content-between project-header">
            <Box sx={{ padding: "0" }}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
              >
                <Tab
                  label="Freight Invoices"
                  {...a11yProps(0)}
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1rem",
                  }}
                />
                <Tab
                  label="Asset Invoices"
                  {...a11yProps(1)}
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1rem",
                  }}
                />
                {type === "Customer" && (
                  <Tab
                    label="Monthly Tier Invoices"
                    {...a11yProps(2)}
                    sx={{
                      fontWeight: "bold",
                      fontSize: "1rem",
                    }}
                  />
                )}
              </Tabs>
            </Box>
            {type === "Customer" && currentCompany.full_access && !isTier ? (
              <img
                src={Add}
                alt="add"
                className="project-delete-image"
                onClick={() => setTrigger(true)}
              />
            ) : (
              type === "Customer" &&
              currentCompany.full_access &&
              isTier && (
                <img
                  src={Add}
                  alt="add"
                  className="project-delete-image"
                  onClick={() => setTierTrigger(true)}
                />
              )
            )}
          </div>
          {currentCompany.full_access && (
            <>
              <CustomTabPanel value={value} index={0}>
                {type === "Customer" ? (
                  <AccountCustomerInvoiceList isAsset={isAsset} isTier={false}/>
                ) : (
                  <AccountVendorInvoiceList isAsset={isAsset} />
                )}
              </CustomTabPanel>
              <CustomTabPanel value={value} index={1}>
                {type === "Customer" ? (
                  <AccountCustomerInvoiceList isAsset={isAsset} isTier={false}/>
                ) : (
                  <AccountVendorInvoiceList isAsset={isAsset}/>
                )}
              </CustomTabPanel>
              {type === "Customer" && isTier && (
                <CustomTabPanel value={value} index={2}>
                  <AccountCustomerInvoiceList isAsset={false} isTier={true}/>
                </CustomTabPanel>
              )}
            </>
          )}
        </Box>
        {trigger && (
          <AddAccountCustomerInvoice
            isAsset={isAsset}
            setTriggerInvoice={setTrigger}
          />
        )}
        {tierTrigger && (
          <AddAccountCustomerTieredInvoice setTriggerInvoice={setTierTrigger} />
        )}
      </>
    );
  }
}
